import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Code, Microscope, Target, Eye, ArrowRight } from 'lucide-react';
import { images } from '../assets/images';
import Section from '../components/Section';

const About: React.FC = () => {
  // Animasyon varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <>
      {/* 1. HERO SECTION: Modern ve Temiz */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* Arka plan efektleri */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-primary-400 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] bg-white rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-display font-bold mb-6 text-white tracking-tight"
            >
              Geleceğin Spor Teknolojisi
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-primary-100 leading-relaxed"
            >
              Yapay zeka ile sınırları kaldırıyor, herkes için daha sağlıklı ve ulaşılabilir bir spor deneyimi tasarlıyoruz.
            </motion.p>
        </div>
      </section>

      {/* 2. BİZ KİMİZ (HİKAYE) BÖLÜMÜ */}
      <Section className="py-20">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Sol Taraf: Görsel */}
          <motion.div 
            className="lg:w-1/2 relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute inset-0 bg-primary-100 rounded-3xl transform rotate-3 scale-105 -z-10"></div>
            <img
              src={images.lotusLogo}
              alt="Lotus Sport Logo"
              className="rounded-3xl shadow-xl w-full object-cover bg-white p-8 h-[400px] object-contain border border-gray-100"
            />
          </motion.div>

          {/* Sağ Taraf: Sadece Hikaye */}
          <div className="lg:w-1/2">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="inline-block py-1 px-3 rounded-full bg-primary-50 text-primary-600 text-sm font-semibold tracking-wide mb-4">
                BİZ KİMİZ?
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Teknoloji ve İnsanı Buluşturan <br/> Yenilikçi Güç
              </h2>
              
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  <b>Lotus Sport Teknoloji</b>, Türkiye'de ve Dünyada fiziksel hareketsizliğe bağlı artan sağlık risklerine karşı etkili, yaygınlaştırılabilir ve sürdürülebilir bir çözüm sunma vizyonuyla yola çıkmıştır. Yapay zeka destekli sağlık ve fitness teknolojileriyle bireylerin yaşam kalitesini artırmayı hedefleyen yenilikçi bir girişimdir.
                </p>
                <p>
                  Kullanıcılarımızın ihtiyaçlarına özel, erişilebilir ve etkili çözümler sunarak, sağlıklı yaşamı herkes için ulaşılabilir kılıyoruz. Teknolojiyi insan odaklı bir yaklaşımla birleştiriyor, bireylerin potansiyellerini en üst düzeye çıkarmalarına yardımcı oluyoruz.
                </p>
                <p>
                  <b>Toplumsal Cinsiyet Eşitliği:</b> Şirket politikamız gereği, ekibimizdeki kadın-erkek dengesini koruyarak toplumsal cinsiyet eşitliğini gözetmekteyiz. Toplam 13 personelimizden 8'i kadın olup, %62 kadın istihdamı ile sektör ortalamasının üzerinde bir profil sergilemekteyiz.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* 3. MİSYON & VİZYON (BÜYÜK KARTLAR) */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Misyon Kartı */}
            <motion.div 
              className="bg-white p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full -mr-8 -mt-8 transition-all group-hover:scale-150 group-hover:bg-primary-100"></div>
              
              <div className="relative z-10">
                <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-primary-200">
                  <Target size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Misyonumuz</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Sağlık ve spor teknolojilerinde yenilikçi çözümler geliştirerek, bireylerin ve toplumun sağlıklı yaşam alışkanlıklarını desteklemek. Gençlik ve Spor Bakanlığı'nın "Herkes İçin Spor" hedeflerini destekleyerek toplum sağlığını korumayı ve uzun vadede sağlık harcamalarının azaltılmasına katkı sağlamayı amaçlıyoruz. Herkesin zamandan ve mekandan bağımsız olarak kişiselleştirilmiş egzersiz ve sağlık hizmetlerine erişimini sağlamak.
                </p>
              </div>
            </motion.div>

            {/* Vizyon Kartı */}
            <motion.div 
              className="bg-primary-900 p-10 rounded-3xl shadow-xl text-white relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Dekoratif Daireler */}
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-800 rounded-full blur-3xl opacity-50 transform translate-x-1/3 translate-y-1/3"></div>
              
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-primary-300 mb-6 border border-white/20">
                  <Eye size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Vizyonumuz</h3>
                <p className="text-lg text-primary-100 leading-relaxed">
                  Teknoloji ve bilimi bir araya getirerek, sürdürülebilir ve kapsayıcı bir sağlık ekosistemi oluşturmak. Lotus Sport Teknoloji olarak, laboratuvar ortamından çıkıp pazarda doğrulanmış yerli girişimimizi, küresel ölçekte etki yaratacak bir markaya dönüştürmeyi hedefliyoruz. Kısa vadede 100.000 aktif kullanıcı ile yerel pazardaki güçlü potansiyelimizi doğrulayarak, global bir oyuncu olmaya hazırlanıyoruz.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 4. AR-GE BÖLÜMÜ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span 
              className="text-primary-600 font-bold tracking-wider uppercase text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              SÜREKLİ GELİŞİM
            </motion.span>
            <motion.h2
              className="text-3xl md:text-4xl font-display font-bold text-dark mt-3 mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ar-Ge Çalışmalarımız
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Brain size={28} />,
                title: "Görüntü İşleme Teknolojisi",
                description: "%100 yerli mühendislik ile geliştirilen özgün yapay zeka (Görüntü İşleme) teknolojisi. Telefon kamerasıyla ortamı tarayarak evdeki sıradan nesneleri egzersiz ekipmanına dönüştürme.",
                bg: "bg-blue-50",
                iconColor: "text-blue-600"
              },
              {
                icon: <Code size={28} />,
                title: "Kişiselleştirilmiş Antrenman",
                description: "Kullanıcının kişisel verilerine göre özelleştirilen antrenman programları. Üç boyutlu robot eğitmen ile görsel sunum ve yapay zeka destekli analizlerle gelişim takibi.",
                bg: "bg-purple-50",
                iconColor: "text-purple-600"
              },
              {
                icon: <Microscope size={28} />,
                title: "Sıfır Ekipman Politikası",
                description: "Pahalı spor aletlerine ihtiyaç duymadan, mevcut eşyaların yeniden işlevlendirilmesi. Kadınlar, yaşlılar, gençler ve kırsal bölgelerde yaşayan bireyler için spora erişim engelini ortadan kaldırma.",
                bg: "bg-teal-50",
                iconColor: "text-teal-600"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className={`w-14 h-14 rounded-xl ${item.bg} ${item.iconColor} flex items-center justify-center mb-6`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                
                <div className="mt-6 flex items-center text-sm font-medium text-gray-400 group cursor-pointer hover:text-primary-600 transition-colors">
                  Detayları İncele <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform"/>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;